"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import AlertDialog from "./AlertDialog";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",

    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
    ...(state.isDisabled && {
      backgroundColor: "#4b5563",
      opacity: 0.5,
      cursor: "not-allowed",
    }),
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

export default function POForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    costCode: "",
    woType: null,
    termsAndConditions: "",
    scopeOfWork: "",
    union: null,
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [unions, setUnions] = useState([]);
  const [project, setProject] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    bidArea: null,
    vendor: null,
    category: null,
    section: null,
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [
          projectResponse,
          vendorsResponse,
          costCodesResponse,
          unionsResponse,
        ] = await Promise.all([
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/costCodes",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/unions",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);
        setProject(projectResponse.data);
        setVendors(vendorsResponse.data);
        setCostCodes(costCodesResponse.data);
        setUnions(unionsResponse.data);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Data Load Error",
          message:
            "Failed to load data: " +
            (err.response?.data?.message || err.message),
        });
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleItemSelect = (itemId) => {
    const item = project?.budgets
      ?.filter((budget) => !budget.isVariance)
      ?.flatMap((budget) => budget.lineItems)
      .find((i) => i._id === itemId);
    if (!item) return;

    const isHRItem = item.uom?.toUpperCase().includes("HR");

    if (isHRItem) {
      if (formData.woType && formData.woType !== "hourly") {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Work Order Type",
          message:
            "Items with 'HR' unit of measure can only be included in hourly work orders. Change the work order type to add this item.",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
        return;
      }
      if (!formData.woType) {
        setFormData({ ...formData, woType: "hourly" });
      }
    }

    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        const newSelected = prev.filter((id) => id !== itemId);
        if (newSelected.length === 0) {
          setSelectedCategory(null);
          setFilters((prevFilters) => ({ ...prevFilters, category: null }));
        }
        return newSelected;
      } else {
        if (!selectedCategory && item.category !== "O") {
          setSelectedCategory(item.category);
          setFilters((prevFilters) => ({
            ...prevFilters,
            category:
              item.category !== "O" ? item.category : prevFilters.category,
          }));
        }
        return [...prev, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    const itemsToSelect = filteredItems.map((item) => item._id);
    const allSelected = itemsToSelect.every((id) => selectedItems.includes(id));

    // Check for mixed 'M' and 'L' items
    const hasM = filteredItems.some((item) => item.category === "M");
    const hasL = filteredItems.some(
      (item) => item.category === "L" || item.category === "O",
    );
    const orderType = determineOrderType();

    // Prevent selecting mixed items without a defined work order type
    if (hasM && hasL && !formData.woType && !orderType) {
      setAlert({
        open: true,
        type: "error",
        title: "Invalid Selection",
        message:
          "Cannot select both Material and Labor items without selecting a Work Order type first.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    if (allSelected) {
      // Deselect all items
      setSelectedItems((prev) =>
        prev.filter((id) => !itemsToSelect.includes(id)),
      );
      if (selectedItems.length === itemsToSelect.length) {
        setSelectedCategory(null);
        setFilters((prevFilters) => ({ ...prevFilters, category: null }));
      }
    } else {
      // Select only items matching the current order type
      const validItems = filteredItems.filter((item) => {
        if (!formData.woType && !orderType) return true; // Allow selection if no type is set and no mixed items
        if (orderType === "material" || formData.woType === null) {
          return item.category === "M";
        }
        return item.category === "L" || item.category === "O";
      });

      if (validItems.length === 0) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Selection",
          message: "No items match the selected order type.",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
        return;
      }

      const newSelectedItems = [
        ...new Set([...selectedItems, ...validItems.map((item) => item._id)]),
      ];
      const firstItem = validItems[0];
      if (!selectedCategory && firstItem && firstItem.category !== "O") {
        setSelectedCategory(firstItem.category);
        setFilters((prevFilters) => ({
          ...prevFilters,
          category:
            firstItem.category !== "O"
              ? firstItem.category
              : prevFilters.category,
        }));
      }
      setSelectedItems(newSelectedItems);
    }
  };

  const isItemLinkedToWO = (budgetItemId) => {
    return (
      project?.workOrders?.some((wo) =>
        wo.lineItems.some((lineItem) => {
          const budgetItemRef =
            typeof lineItem.budgetItem === "object" && lineItem.budgetItem.$oid
              ? lineItem.budgetItem.$oid
              : lineItem.budgetItem;
          return budgetItemRef === budgetItemId;
        }),
      ) || false
    );
  };

  const isItemLinkedToPO = (budgetItemId) => {
    const item = project?.budgets
      ?.filter((budget) => !budget.isVariance)
      ?.flatMap((budget) => budget.lineItems)
      ?.find((i) => i._id === budgetItemId);
    return item?.linkedPO || false;
  };
  const filteredItems = (
    project?.budgets
      ?.filter((budget) => !budget.isVariance)
      ?.flatMap((budget) =>
        budget.lineItems.map((item) => ({
          ...item,
          source: budget.isChangeOrder ? `changeOrder` : `budget`,
          budgetId: budget._id,
        })),
      ) || []
  )
    .filter((item) => {
      const isLinkedPO = isItemLinkedToPO(item._id);
      const isLinkedWO = isItemLinkedToWO(item._id);
      return !isLinkedPO && (item.category !== "L" || !isLinkedWO);
    })
    .filter((item) => {
      if (!selectedCategory) return true;
      return item.category === selectedCategory || item.category === "O";
    })
    .filter((item) => {
      if (!filters.bidArea) return true;
      return item.bidArea === filters.bidArea;
    })
    .filter((item) => {
      if (!filters.vendor || item.category !== "M") return true;
      const vendorId =
        item.vendor && typeof item.vendor === "object"
          ? item.vendor._id
          : item.vendor || null;
      return vendorId === filters.vendor;
    })
    .filter((item) => {
      if (!filters.category) return true;
      return item.category === filters.category;
    })
    .filter((item) => {
      if (!filters.section) return true;
      return item.sectionNumber === filters.section;
    });

  const getFilterOptions = (field) => {
    const items =
      project?.budgets
        ?.filter((budget) => !budget.isVariance)
        ?.flatMap((budget) => budget.lineItems) || [];

    if (field === "vendor") {
      return [
        { value: null, label: "Unassigned" },
        ...vendors.map((vendor) => ({
          value: vendor._id,
          label: vendor.companyName,
        })),
      ];
    }

    const uniqueValues = [
      ...new Set(
        items
          .filter((item) => {
            const isLinkedPO = isItemLinkedToPO(item._id);
            const isLinkedWO = isItemLinkedToWO(item._id);
            return !isLinkedPO && (item.category !== "L" || !isLinkedWO);
          })
          .map((item) => item[field])
          .filter((value) => value),
      ),
    ];

    return uniqueValues.map((value) => ({
      value,
      label: value || "N/A",
    }));
  };

  const handleFilterChange = (filterType, selectedOption) => {
    setFilters({
      ...filters,
      [filterType]: selectedOption ? selectedOption.value : null,
    });
  };

  const determineOrderType = () => {
    if (selectedItems.length === 0) return null;
    const selected = project?.budgets
      ?.flatMap((budget) => budget.lineItems)
      .filter((item) => selectedItems.includes(item._id));
    const hasM = selected.some((item) => item.category === "M");
    const hasL = selected.some(
      (item) => item.category === "L" || item.category === "O",
    );
    return hasM ? "material" : hasL ? "labor" : "material";
  };

  const hasHRItems = () => {
    const selected = project?.budgets
      ?.flatMap((budget) => budget.lineItems)
      .filter((item) => selectedItems.includes(item._id));
    return selected?.some((item) => item.uom?.toUpperCase().includes("HR"));
  };

  const validateSelection = () => {
    if (selectedItems.length === 0) return "No items selected.";
    const selected = project?.budgets
      ?.flatMap((budget) => budget.lineItems)
      .filter((item) => selectedItems.includes(item._id));
    if (selected.length === 0) return "No valid items selected.";

    const hasM = selected.some((item) => item.category === "M");
    const hasL = selected.some((item) => item.category === "L");
    const hasHR = selected.some((item) =>
      item.uom?.toUpperCase().includes("HR"),
    );

    if (hasM && hasL) {
      return "Cannot mix 'M' and 'L' categories in the same order.";
    }
    if (hasL && !formData.woType) {
      return "Please select a Work Order type for labor items.";
    }
    if (hasHR && formData.woType !== "hourly") {
      return "Items with 'HR' UOM can only be included in Hourly work orders.";
    }
    if (
      hasL &&
      formData.woType === "subcontractor" &&
      (!formData.termsAndConditions || !formData.scopeOfWork)
    ) {
      return "Terms & Conditions and Scope of Work are required for subcontractor work orders.";
    }
    if (
      hasL &&
      (formData.woType === "hourly" || formData.woType === "piecework") &&
      !formData.union
    ) {
      return "Union selection is required for hourly or piecework work orders.";
    }
    return null;
  };
  const getUserIdFromToken = () => {
    const token = localStorage.getItem("token");
    console.log(token);
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log(decoded);
        return decoded.id;
      } catch (err) {
        return null;
      }
    }
    return null;
  };

  const handleWorkOrderTypeChange = (selectedOption) => {
    const newWoType = selectedOption ? selectedOption.value : null;
    const budgetItems =
      project?.budgets?.flatMap((budget) => budget.lineItems) || [];

    // Clear HR items if switching away from hourly
    if (newWoType !== "hourly") {
      const newSelectedItems = selectedItems.filter((itemId) => {
        const item = budgetItems.find((i) => i._id === itemId);
        return !item?.uom?.toUpperCase().includes("HR");
      });
      setSelectedItems(newSelectedItems);
      if (newSelectedItems.length === 0) {
        setSelectedCategory(null);
        setFilters((prevFilters) => ({ ...prevFilters, category: null }));
      }
    }

    // Ensure labor-specific fields are reset appropriately
    setFormData({
      ...formData,
      woType: newWoType,
      union:
        newWoType === "subcontractor" ? null : formData.union || "non-union",
      termsAndConditions:
        newWoType === "subcontractor" ? formData.termsAndConditions : "",
      scopeOfWork: newWoType === "subcontractor" ? formData.scopeOfWork : "",
    });

    // If selecting labor items, ensure category filter is set to 'L' or 'O'
    if (newWoType && selectedItems.length > 0) {
      const selected = budgetItems.filter((item) =>
        selectedItems.includes(item._id),
      );
      const hasL = selected.some(
        (item) => item.category === "L" || item.category === "O",
      );
      if (hasL && !selectedCategory) {
        setSelectedCategory("L");
        setFilters((prevFilters) => ({
          ...prevFilters,
          category: "L",
        }));
      }
    }
  };

  const handleUnionChange = (selectedOption) => {
    setFormData({
      ...formData,
      union: selectedOption ? selectedOption.value : null,
    });
  };

  const formatNumberWithCommas = (number) => {
    return Number(number).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const normalizeSize = (size) => {
    if (!size) return "";
    return size
      .toLowerCase()
      .trim()
      .replace(/[\s\u00A0]+/g, "")
      .replace(/[x×]/i, "x");
  };

  const normalizeItemCode = (itemCode) => {
    if (!itemCode) return "";
    return itemCode
      .replace(/\\"/g, '"')
      .replace(/"/g, '"')
      .trim()
      .replace(/[\s\u00A0]+/g, " ");
  };

  const findProjectItemId = (budgetItem, orderType) => {
    const normalizedBudgetItemSize = normalizeSize(budgetItem.size || "");
    const normalizedBudgetItemCode = normalizeItemCode(
      budgetItem.itemCode || "",
    );

    const projectItem = project?.projectItems?.find((pi) => {
      const normalizedProjectItemSize = normalizeSize(pi.size || "");
      const normalizedProjectItemCode = normalizeItemCode(pi.itemCode || "");
      const normalizedBudgetBidArea = budgetItem.bidArea || "";
      const normalizedProjectBidArea = pi.bidArea || "";
      return (
        normalizedProjectItemCode === normalizedBudgetItemCode &&
        normalizedProjectItemSize === normalizedBudgetItemSize &&
        normalizedProjectBidArea === normalizedBudgetBidArea &&
        pi.itemType === orderType
      );
    });

    return projectItem ? projectItem._id.$oid || projectItem._id : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Set loading state to true to disable the button
    setIsSubmitting(true);

    const validationError = validateSelection();
    if (validationError) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: validationError,
      });
      // Reset loading state on validation error
      setIsSubmitting(false);
      return;
    }
    if (!formData.title || !formData.costCode) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Title and Cost Code are required.",
      });
      // Reset loading state on validation error
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const userId = getUserIdFromToken();
      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      const budgetItems =
        project?.budgets
          ?.filter((budget) => !budget.isVariance)
          ?.flatMap((budget) => budget.lineItems) || [];
      const selectedBudgetItems = budgetItems.filter((item) =>
        selectedItems.includes(item._id),
      );
      const orderType = determineOrderType();

      const projectItemIdMap = new Map();
      for (const item of selectedBudgetItems) {
        const projectItemId = findProjectItemId(item, orderType);
        projectItemIdMap.set(item._id, projectItemId);
      }

      if (orderType === "material") {
        // Calculate estimatedCost for material POs
        const estimatedCost = selectedBudgetItems.reduce(
          (sum, item) =>
            sum + (item.requiredQty || item.quantity || 1) * (item.cost || 0), // cost only — never price
          0,
        );

        // Validate that estimatedCost is greater than 0
        if (estimatedCost <= 0) {
          setAlert({
            open: true,
            type: "error",
            title: "Validation Error",
            message:
              "Estimated cost must be greater than zero. Please ensure selected items have valid quantities and costs.",
          });
          // Reset loading state on validation error
          setIsSubmitting(false);
          return;
        }

        const poData = {
          title: formData.title,
          type: "material",
          costCode: formData.costCode,
          estimatedCost: estimatedCost,
          project: id,
          createdBy: userId,
          lineItems: selectedBudgetItems.map((item) => ({
            budgetItem: item._id,
            title: item.title || item.costCodeDescription,
            costCode: formData.costCode,
            quantity: item.requiredQty || item.quantity || 1,
            unitCost: item.cost || 0, // cost only — never price
            vendor:
              item.vendor && typeof item.vendor === "object"
                ? item.vendor._id
                : item.vendor || null,
            itemCode: item.itemCode || "",
            itemDescription: item.itemDescription || "",
            size: item.size || "",
            requiredQty: item.requiredQty || item.quantity || 0,
            container: item.container || "",
            bidArea: item.bidArea || "",
            isChangeOrder: item.isChangeOrder || false,
            projectItem: projectItemIdMap.get(item._id),
          })),
        };

        const response = await axios.post(
          `https://erp-production-72da01c8e651.herokuapp.com/api/purchaseOrders`,
          poData,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setProject((prev) => ({
          ...prev,
          purchaseOrders: response.data.purchaseOrders || prev.purchaseOrders,
        }));
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "PO created successfully",
          onConfirm: () => {
            setAlert({ ...alert, open: false });
            navigate(`/Projects/${id}`, { state: { initialTab: "POs" } });
          },
        });
      } else if (orderType === "labor") {
        const woData = {
          title: formData.title,
          type: formData.woType,
          project: id,
          costCode: formData.costCode,
          estimatedCost: selectedBudgetItems.reduce(
            (sum, item) => sum + (item.amount || item.quantity * item.unitCost),
            0,
          ),
          createdBy: userId,
          lineItems: selectedBudgetItems.map((item) => ({
            budgetItem: item._id,
            title: item.title || item.costCodeDescription,
            costCode: formData.costCode,
            qtyEstimated: item.quantity || 1,
            unitCost:
              item.unitCost ||
              item.price ||
              item.amount / (item.quantity || 1) ||
              0,
            uom: item.uom || "",
            laborHoursEstimated: item.laborHours || 0,
            bidArea: item.bidArea || "",
            isChangeOrder: item.isChangeOrder || false,
            projectItem: projectItemIdMap.get(item._id),
          })),
          ...(formData.woType === "subcontractor" && {
            termsAndConditions: formData.termsAndConditions,
            scopeOfWork: formData.scopeOfWork,
          }),
          ...(formData.woType === "hourly" || formData.woType === "piecework"
            ? { union: formData.union || "non-union" }
            : {}),
          bidArea: selectedBudgetItems[0]?.bidArea || "",
        };

        const response = await axios.post(
          `https://erp-production-72da01c8e651.herokuapp.com/api/workorders`,
          woData,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setProject((prev) => ({
          ...prev,
          workOrders: response.data.workOrders || prev.workOrders,
        }));
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "WO created successfully",
          onConfirm: () => {
            setAlert({ ...alert, open: false });
            navigate(`/Projects/${id}`, {
              state: { initialTab: "Work Orders" },
            });
          },
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Submission Error",
        message:
          "Failed to generate order: " +
          (err.response?.data?.message || err.message),
      });
    } finally {
      // Reset loading state after submission completes or errors
      setIsSubmitting(false);
    }
  };

  const costCodeOptions = costCodes.map((cc) => ({
    value: cc._id,
    label: cc.code || "Unnamed Cost Code",
  }));

  const woTypeOptions = [
    { value: "hourly", label: "Hourly" },
    { value: "piecework", label: "Piecework" },
    { value: "subcontractor", label: "Subcontractor" },
  ].filter((option) => {
    if (!hasHRItems()) return true;
    return option.value === "hourly";
  });

  const unionOptions = [
    { value: "non-union", label: "Non-Union" },
    ...unions.map((union) => ({
      value: union.title,
      label: union.title,
    })),
  ];

  // Add this new function before handleFilterChange
  const clearAllFilters = () => {
    setFilters({
      bidArea: null,
      vendor: null,
      category: null,
      section: null,
    });
    setSelectedCategory(null);
    setSelectedItems([]); // Clear selected items
  };
  if (!project)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;

  const orderType = determineOrderType();
  const selectedBudgetItems = filteredItems.filter((item) =>
    selectedItems.includes(item._id),
  );

  const getOrderTitle = () => {
    if (orderType === null) return "Purchase Order or Work Order";
    return orderType === "material" ? "Purchase Order" : "Work Order";
  };

  return (
    <div className="mx-auto px-4 py-6">
      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => {
          setAlert({ ...alert, open: false });
          if (alert.type === "success") {
            const initialTab =
              orderType === "labor" ? "Work Orders" : "Purchase Orders";
            navigate(`/Projects/${id}`, { state: { initialTab } });
          }
        }}
        confirmText="OK"
        cancelText="Close"
      />
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-white">
            Generate {getOrderTitle()}
          </h1>
          <button
            onClick={() => navigate(`/projects/${id}`)}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">Details</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Title"
                className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Cost Code
              </label>
              <Select
                options={costCodeOptions}
                value={
                  costCodeOptions.find(
                    (option) => option.value === formData.costCode,
                  ) || null
                }
                onChange={(selectedOption) =>
                  setFormData({
                    ...formData,
                    costCode: selectedOption ? selectedOption.value : "",
                  })
                }
                placeholder="Select Cost Code..."
                isClearable
                className="mt-1"
                styles={customStyles}
                required
              />
            </div>
            {orderType === "labor" && (
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Work Order Type
                </label>
                <Select
                  options={woTypeOptions}
                  value={
                    woTypeOptions.find(
                      (option) => option.value === formData.woType,
                    ) || null
                  }
                  onChange={handleWorkOrderTypeChange}
                  placeholder="Select WO Type..."
                  isClearable
                  className="mt-1"
                  styles={customStyles}
                  required
                />
              </div>
            )}
            {orderType === "labor" &&
              (formData.woType === "hourly" ||
                formData.woType === "piecework") && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Union
                  </label>
                  <Select
                    options={unionOptions}
                    value={
                      unionOptions.find(
                        (option) => option.value === formData.union,
                      ) || null
                    }
                    onChange={handleUnionChange}
                    placeholder="Select Union..."
                    isClearable
                    className="mt-1"
                    styles={customStyles}
                  />
                </div>
              )}
            {orderType === "labor" && formData.woType === "subcontractor" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Terms & Conditions
                  </label>
                  <textarea
                    name="termsAndConditions"
                    value={formData.termsAndConditions}
                    onChange={handleChange}
                    placeholder="Enter terms and conditions..."
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Scope of Work
                  </label>
                  <textarea
                    name="scopeOfWork"
                    value={formData.scopeOfWork}
                    onChange={handleChange}
                    placeholder="Enter scope of work..."
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">
            Select Items
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-[140px_auto_auto_auto] gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Filter by Category
              </label>
              <Select
                options={getFilterOptions("category")}
                value={
                  getFilterOptions("category").find(
                    (option) => option.value === filters.category,
                  ) || null
                }
                onChange={(option) => handleFilterChange("category", option)}
                placeholder="Select Category..."
                isClearable
                isDisabled={selectedCategory !== null}
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Filter by Section
              </label>
              <Select
                options={getFilterOptions("sectionNumber")}
                value={
                  getFilterOptions("sectionNumber").find(
                    (option) => option.value === filters.section,
                  ) || null
                }
                onChange={(option) => handleFilterChange("section", option)}
                placeholder="Select Section..."
                isClearable
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Filter by Vendor (Material Items Only)
              </label>
              <Select
                options={getFilterOptions("vendor")}
                value={
                  getFilterOptions("vendor").find(
                    (option) => option.value === filters.vendor,
                  ) || null
                }
                onChange={(option) => handleFilterChange("vendor", option)}
                placeholder="Select Vendor..."
                isClearable
                isDisabled={orderType === "labor"}
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Filter by Bid Area
              </label>
              <Select
                options={getFilterOptions("bidArea")}
                value={
                  getFilterOptions("bidArea").find(
                    (option) => option.value === filters.bidArea,
                  ) || null
                }
                onChange={(option) => handleFilterChange("bidArea", option)}
                placeholder="Select Bid Area..."
                isClearable
                className="mt-1"
                styles={customStyles}
              />
            </div>
          </div>

          <div className="mb-4 flex space-x-4">
            <button
              type="button"
              onClick={() => handleSelectAll()}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
            >
              {filteredItems.every((item) => selectedItems.includes(item._id))
                ? "Deselect All"
                : "Select All"}
            </button>
            <button
              type="button"
              onClick={clearAllFilters}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
          {filteredItems.length === 0 ? (
            <p className="text-gray-400">
              No items available without a linked PO or WO.
            </p>
          ) : (
            <div className=" rounded-lg shadow-xl">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-800 rounded-tl-lg">
                      Select
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Section
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cat
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    {orderType !== "labor" ? (
                      <>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Size
                        </th>
                        <th className=" py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Req. Qty
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Container
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                          Bid Area
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          UOM
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Labor Hours
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                          Bid Area
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {filteredItems.map((item) => {
                    const vendorId =
                      item.vendor && typeof item.vendor === "object"
                        ? item.vendor._id
                        : item.vendor || null;
                    const vendorName =
                      vendors.find((v) => v._id === vendorId)?.companyName ||
                      "N/A";
                    const isLinkedToPO = isItemLinkedToPO(item._id); // Check if item has a linked PO

                    return (
                      <tr
                        key={item._id}
                        className={
                          selectedItems.includes(item._id)
                            ? "bg-orange-500"
                            : isLinkedToPO
                              ? "bg-gray-800 opacity-50" // Gray out rows with linked POs
                              : "hover:bg-gray-600 transition-colors duration-200"
                        }
                        title={
                          isLinkedToPO
                            ? "This item is already linked to a Purchase Order"
                            : ""
                        }
                      >
                        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200 sticky left-0 bg-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item._id)}
                            onChange={() =>
                              !isLinkedToPO && handleItemSelect(item._id)
                            } // Prevent interaction if linked to PO
                            disabled={isLinkedToPO} // Disable checkbox for linked items
                            className={`h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded ${
                              isLinkedToPO ? "cursor-not-allowed" : ""
                            }`}
                            title={
                              isLinkedToPO
                                ? "Already linked to a Purchase Order"
                                : ""
                            }
                          />
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                          {project.budgets.find(
                            (budget) => budget._id === item.budgetId,
                          )?.title
                            ? `${
                                item.source === "changeOrder"
                                  ? "CO - "
                                  : "Budget"
                              } ${
                                project.budgets.find(
                                  (budget) => budget._id === item.budgetId,
                                ).title
                              }`
                            : item.source === "changeOrder"
                              ? "Change Order"
                              : "Budget"}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                          {item.sectionNumber || "N/A"}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                          {item.category}
                        </td>
                        <td
                          className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 max-w-[20ch] overflow-hidden"
                          title={item.title || item.costCodeDescription}
                        >
                          {(item.title || item.costCodeDescription).length > 20
                            ? `${(item.title || item.costCodeDescription).slice(
                                0,
                                40,
                              )}..`
                            : item.title || item.costCodeDescription}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          $
                          {(
                            item.amount ||
                            (item.requiredQty || item.quantity || 1) *
                              (item.cost || 0)
                          ).toFixed(2)}
                        </td>
                        {orderType !== "labor" ? (
                          <>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {vendorName}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                              {item.size || "N/A"}
                            </td>
                            <td className="py-2 whitespace-nowrap text-sm text-white">
                              {item.requiredQty || item.quantity || 0}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {item.container || "N/A"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {item.bidArea || "N/A"}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {item.quantity || 0}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {item.uom || "N/A"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {item.laborHours || 0}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-white">
                              {item.bidArea || "N/A"}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {orderType === "labor" &&
            (formData.woType === "hourly" ||
              formData.woType === "piecework") && (
              <div className="mt-4">
                <h3 className="text-md font-semibold text-gray-400">
                  Estimated Totals
                </h3>
                <p className="text-gray-200">
                  Total Labor Hours:{" "}
                  {selectedBudgetItems
                    .reduce((sum, item) => sum + (item.laborHours || 0), 0)
                    .toFixed(2)}
                </p>
                <p className="text-gray-200">
                  Total Quantity:{" "}
                  {selectedBudgetItems
                    .reduce((sum, item) => sum + (item.quantity || 0), 0)
                    .toFixed(2)}{" "}
                  {selectedBudgetItems[0]?.uom || ""}
                </p>
                <p className="text-gray-200">
                  Total Estimated Cost: $
                  {formatNumberWithCommas(
                    selectedBudgetItems
                      .reduce(
                        (sum, item) =>
                          sum + (item.amount || item.quantity * item.unitCost),
                        0,
                      )
                      .toFixed(2),
                  )}
                </p>
              </div>
            )}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/projects/${id}`)}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
              isSubmitting
                ? "bg-orange-300 cursor-not-allowed"
                : "bg-orange-400 hover:bg-orange-500"
            }`}
          >
            {isSubmitting ? "Generating..." : `Generate ${getOrderTitle()}`}
          </button>
        </div>
      </form>
    </div>
  );
}
